import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const emptyForm = {
  course_id: '',
  title: '',
  question: '',
  deadline: '',
  max_marks: '',
};

const ATTACHMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AdminAssignmentForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]); // File[]
  const [existingAttachments, setExistingAttachments] = useState([]); // { id, file_path, original_name }[]
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchCourses = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/courses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setCourses(response.data.courses || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchBatchesForCourse = useCallback(async (courseId) => {
    if (!courseId) { setBatches([]); return; }
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/batches/${courseId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setBatches(response.data.batches || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchAssignment = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/assignments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        const a = response.data.assignment;
        setForm({
          course_id: a.course_id,
          title: a.title,
          question: a.question || '',
          deadline: a.deadline ? a.deadline.slice(0, 16) : '',
          max_marks: a.max_marks ?? '',
        });
        setSelectedBatchIds((response.data.batch_ids || []).map(String));
        setExistingAttachments(response.data.attachments || []);
        fetchBatchesForCourse(a.course_id);
      } else {
        setError(response.data.message || 'Failed to fetch assignment.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching assignment.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => { fetchAssignment(); }, [fetchAssignment]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCourseChange = (courseId) => {
    setField('course_id', courseId);
    setSelectedBatchIds([]);
    fetchBatchesForCourse(courseId);
  };

  const toggleBatch = (batchId) => {
    const idStr = String(batchId);
    setSelectedBatchIds((prev) => (prev.includes(idStr) ? prev.filter((b) => b !== idStr) : [...prev, idStr]));
  };

  const handleAttachmentsChange = (e) => {
    const picked = Array.from(e.target.files || []);
    // append, de-dupe by name+size
    setNewAttachments((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}_${f.size}`));
      return [...prev, ...picked.filter((f) => !seen.has(`${f.name}_${f.size}`))];
    });
    e.target.value = ''; // allow re-picking the same file
  };

  const removeNewAttachment = (idx) => {
    setNewAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeExistingAttachment = (attId) => {
    setRemovedAttachmentIds((prev) => [...prev, attId]);
    setExistingAttachments((prev) => prev.filter((a) => a.id !== attId));
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!form.course_id || !form.title.trim()) {
      setError('Course and title are required.');
      return;
    }
    if (!form.question || !form.question.trim()) {
      setError('The assignment question cannot be empty.');
      return;
    }
    if (selectedBatchIds.length === 0) {
      setError('Select at least one batch.');
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };

      const fd = new FormData();
      fd.append('course_id', form.course_id);
      fd.append('title', form.title.trim());
      fd.append('question', form.question);
      fd.append('batch_ids', JSON.stringify(selectedBatchIds.map(Number)));
      fd.append('deadline', form.deadline || '');
      fd.append('max_marks', form.max_marks === '' ? '' : String(Number(form.max_marks)));
      newAttachments.forEach((file) => fd.append('attachments', file));
      if (isEdit) fd.append('removed_attachment_ids', JSON.stringify(removedAttachmentIds));

      if (isEdit) {
        const response = await axios.put(`${apiUrl}/api/admin/assignments/${id}`, fd, { headers });
        if (response.data.success) {
          setSuccess('Assignment updated successfully.');
          setNewAttachments([]);
          setRemovedAttachmentIds([]);
          fetchAssignment();
        } else {
          setError(response.data.message || 'Failed to update assignment.');
        }
      } else {
        const response = await axios.post(`${apiUrl}/api/admin/assignments`, fd, { headers });
        if (response.data.success) {
          setSuccess('Assignment created successfully.');
          setTimeout(() => navigate('/admin-assignments'), 800);
        } else {
          setError(response.data.message || 'Failed to create assignment.');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving assignment.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-assignments" className="text-gray-200 fw-normal text-15 hover-text-main-600">Assignments</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">{isEdit ? 'Edit Assignment' : 'Create Assignment'}</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-20">
                <div className="col-sm-6">
                  <label className="h5 mb-8 fw-semibold font-heading">Course <span className="text-red-500">*</span></label>
                  <select className="form-control py-11" value={form.course_id} onChange={(e) => handleCourseChange(e.target.value)} disabled={saving}>
                    <option value="">Select course...</option>
                    {courses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                  </select>
                </div>
                <div className="col-sm-6">
                  <label className="h5 mb-8 fw-semibold font-heading">Assignment Title <span className="text-red-500">*</span></label>
                  <input type="text" className="form-control py-11" value={form.title} onChange={(e) => setField('title', e.target.value)} disabled={saving} />
                </div>

                <div className="col-12">
                  <label className="h5 mb-8 fw-semibold font-heading d-block">Question <span className="text-red-500">*</span></label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={form.question}
                    onChange={(evt, editor) => setField('question', editor.getData())}
                    config={{ toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'] }}
                  />
                </div>

                <div className="col-sm-4">
                  <label className="h5 mb-8 fw-semibold font-heading">Deadline</label>
                  <input type="datetime-local" className="form-control py-11" placeholder="Leave blank = no deadline"
                    value={form.deadline} onChange={(e) => setField('deadline', e.target.value)} disabled={saving} />
                </div>
                <div className="col-sm-4">
                  <label className="h5 mb-8 fw-semibold font-heading">Max Marks</label>
                  <input type="number" min="0" step="0.5" className="form-control py-11" placeholder="Optional"
                    value={form.max_marks} onChange={(e) => setField('max_marks', e.target.value)} disabled={saving} />
                </div>

                <div className="col-12">
                  <label className="h5 mb-8 fw-semibold font-heading d-block">Assign to Batches <span className="text-red-500">*</span></label>
                  {!form.course_id ? (
                    <div className="text-gray-400 py-8">Select a course first.</div>
                  ) : batches.length === 0 ? (
                    <div className="text-gray-400 py-8">No batches found for this course.</div>
                  ) : (
                    <div className="row g-8">
                      {batches.map((b) => (
                        <div className="col-sm-4" key={b.id}>
                          <div className="form-check">
                            <input
                              className="form-check-input border-gray-200 rounded-4"
                              type="checkbox"
                              id={`batch-${b.id}`}
                              checked={selectedBatchIds.includes(String(b.id))}
                              onChange={() => toggleBatch(b.id)}
                              disabled={saving}
                            />
                            <label className="form-check-label" htmlFor={`batch-${b.id}`}>{b.batchname}</label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label className="h5 mb-8 fw-semibold font-heading d-block">Supporting Documents / Images</label>
                  <div className="text-13 text-gray-500 mb-8">
                    Optional. Students will be able to view these alongside the assignment. Images, PDF, Word, Excel, PowerPoint or text — up to 15 MB each.
                  </div>
                  <input
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    className="form-control py-9"
                    style={{ maxWidth: 420 }}
                    onChange={handleAttachmentsChange}
                    disabled={saving}
                  />

                  {existingAttachments.length > 0 && (
                    <div className="mt-12">
                      <div className="text-13 text-gray-500 mb-4">Current attachments</div>
                      <ul className="list-unstyled mb-0">
                        {existingAttachments.map((a) => (
                          <li key={a.id} className="d-flex align-items-center gap-8 py-4">
                            <i className="ph ph-paperclip text-gray-400" />
                            <a href={`${apiUrl}/${a.file_path}`} target="_blank" rel="noreferrer" className="text-main-600 text-decoration-underline text-14">
                              {a.original_name}
                            </a>
                            <button
                              type="button"
                              className="btn btn-sm text-danger p-0 ms-2"
                              onClick={() => removeExistingAttachment(a.id)}
                              disabled={saving}
                              title="Remove this attachment on save"
                            >
                              <i className="ph ph-x-circle text-18" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {newAttachments.length > 0 && (
                    <div className="mt-12">
                      <div className="text-13 text-gray-500 mb-4">To be uploaded</div>
                      <ul className="list-unstyled mb-0">
                        {newAttachments.map((f, idx) => (
                          <li key={`${f.name}_${f.size}_${idx}`} className="d-flex align-items-center gap-8 py-4">
                            <i className="ph ph-file-plus text-gray-400" />
                            <span className="text-14 text-gray-300">{f.name}</span>
                            <span className="text-12 text-gray-400">{formatBytes(f.size)}</span>
                            <button
                              type="button"
                              className="btn btn-sm text-danger p-0 ms-2"
                              onClick={() => removeNewAttachment(idx)}
                              disabled={saving}
                              title="Remove"
                            >
                              <i className="ph ph-x-circle text-18" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-8 mb-24">
            <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={() => navigate('/admin-assignments')}>Back to List</button>
            <button type="button" className="btn btn-main rounded-pill py-9" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Assignment'}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAssignmentForm;
